import { type ReactElement, type ReactNode, type RefObject, useState } from 'react'
import useEvent from 'react-use-event-hook'
import { Popover as BasePopover } from '@base-ui/react/popover'
import type { TooltipPositionerProps } from '@base-ui/react/tooltip'
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'

import { ChevronDownIcon } from '@gto/icons-react/outline/chevron-down'
import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { Popover } from '@gto/ui/popover'
import { typography } from '@gto/ui/typography'

import { useCursorAnchor } from './use-cursor-anchor'

// Open / close animation for the DESKTOP popup, which talks to Base UI directly
// and so had none. The mobile branch renders the DS `Popover`, whose own popup
// already carries this, and inherits it.
//
// Base UI marks the popup with `data-starting-style` on mount and
// `data-ending-style` during teardown; those frames are the from/to states the CSS
// transition interpolates against the resting values, and Base UI holds the element
// mounted until the transition ends. `--transform-origin` comes from the Positioner,
// so the popup grows out of the side it is anchored to. Same idiom, duration and
// easing as Popover and Menu — a tooltip should not pop in harder than a menu.
const POPUP_ANIMATION = [
  'origin-[var(--transform-origin)] transition duration-150 ease-out',
  'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
  'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
]

export type TooltipProps = {
  children: ReactElement
  content: ReactNode
  className?: string
  placement?: TooltipPositionerProps['side']
  align?: TooltipPositionerProps['align']
  open?: boolean
  disabled?: boolean
  delay?: number
  closeDelay?: number
  /**
   * Whether clicking the trigger closes the tooltip. Defaults to Base UI's
   * `true`; set to `false` when the trigger has its own click handler that
   * should not race with Base UI's auto-close. When on, an uncontrolled
   * tooltip also stays closed under the resting pointer after the click
   * (released on pointer leave / blur); controlled `open` is never affected.
   */
  closeOnClick?: boolean
  /**
   * Whether the mobile popover trigger renders native button semantics. The
   * legacy default treats composite children as native buttons; set `false`
   * when a composite child renders a non-native control such as `Chip`.
   */
  mobileTriggerIsNativeButton?: boolean
  followCursor?: boolean
  sideOffset?: number
  arrow?: boolean
  hoverable?: boolean
  anchor?: RefObject<Element | null>
  /**
   * Label size. `small` is 12px, `large` is 14px. Unset, it is `large` below
   * 600px, where 12px is too small to read on a phone (PLO-1347).
   */
  labelSize?: 'small' | 'large'
  onOpenChange?: (open: boolean) => void
}

const LARGE_LABEL_BELOW_PX = 600

const LABEL_VARIANT = {
  small: 'tooltip/label-small',
  large: 'tooltip/label-large',
} as const satisfies Record<NonNullable<TooltipProps['labelSize']>, string>

export const Tooltip = ({
  children,
  content,
  className,
  placement = 'top',
  align,
  open,
  disabled,
  delay = 300,
  closeDelay,
  closeOnClick,
  mobileTriggerIsNativeButton,
  sideOffset = 8,
  followCursor,
  arrow,
  hoverable,
  anchor,
  labelSize,
  onOpenChange,
}: TooltipProps) => {
  const isNarrowScreen = useMediaQuery('down', LARGE_LABEL_BELOW_PX)
  const labelTypography =
    typography[LABEL_VARIANT[labelSize ?? (isNarrowScreen ? 'large' : 'small')]]
  const isDisabled = disabled === true || content == null
  // A click closes the tooltip, but the pointer still resting on the trigger
  // would immediately re-open it — over whatever the click just revealed.
  // Latch closed until the pointer leaves (or focus moves), unless the
  // consumer opted out of close-on-click entirely.
  const [clickLatched, setClickLatched] = useState(false)
  const latchesOnClick = closeOnClick !== false && open === undefined
  const armClickLatch = useEvent(() => {
    if (latchesOnClick) {
      setClickLatched(true)
    }
  })
  const releaseClickLatch = useEvent(() => {
    if (clickLatched) {
      setClickLatched(false)
    }
  })
  const popoverTriggerIsButton =
    mobileTriggerIsNativeButton ?? (typeof children.type !== 'string' || children.type === 'button')
  const isFollowCursor = followCursor === true
  const isMobile = useMediaQuery('down', 'sm')
  const [mobileOpen, setMobileOpen] = useState(false)

  const cursorAnchor = useCursorAnchor(isFollowCursor)

  // When following the cursor, drive positioning through a virtual anchor
  // that always reports the latest pointer position, and disable Base UI's
  // own cursor tracking — its `useClientPoint` integration caches the
  // previous position across opens and ships it as the first frame of the
  // next open. The virtual anchor sidesteps that.
  const positionerAnchor = isFollowCursor ? cursorAnchor.virtualElement : anchor

  const handleOpenChange = useEvent((nextOpen: boolean) => {
    if (isMobile && open === undefined) {
      setMobileOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  })

  if (isMobile && !isDisabled) {
    return (
      <Popover.Root
        open={open ?? mobileOpen}
        onOpenChange={handleOpenChange}
      >
        {/* Base UI's trigger, not the DS one: a help tooltip must not tint an enclosing Field
            label through the popup-open marker. */}
        <BasePopover.Trigger
          delay={0}
          nativeButton={popoverTriggerIsButton}
          render={children}
          onClick={
            closeOnClick === false
              ? (event) => {
                  event.preventDefault()
                }
              : undefined
          }
        />
        <Popover.Portal>
          <Popover.Positioner
            align={align}
            anchor={anchor}
            className="z-tooltip"
            side={placement}
            sideOffset={sideOffset}
          >
            <Popover.Popup
              className={cn(
                labelTypography,
                'max-w-80 rounded-sm border border-secondary-main bg-paper-elevation4 p-2 text-text-primary shadow-8',
                className,
              )}
            >
              {content}
              {arrow === true && (
                <Popover.Arrow className="flex data-[side=bottom]:top-[-11px] data-[side=left]:right-[-11px] data-[side=right]:left-[-11px] data-[side=top]:bottom-[-11px] data-[side=bottom]:rotate-180 data-[side=left]:-rotate-90 data-[side=right]:rotate-90 data-[side=top]:rotate-0">
                  <ChevronDownIcon className="size-[20px] text-secondary-main" />
                </Popover.Arrow>
              )}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    )
  }

  return (
    <BaseTooltip.Root
      disableHoverablePopup={hoverable !== true}
      // The latch only governs UNCONTROLLED tooltips: Base UI computes
      // `open = !disabled && openState`, so disabling would force-close a
      // controlled `open` the consumer is deliberately showing.
      disabled={isDisabled || (open === undefined && clickLatched)}
      open={open}
      trackCursorAxis="none"
      onOpenChange={handleOpenChange}
    >
      <BaseTooltip.Trigger
        ref={isFollowCursor ? cursorAnchor.triggerRefCallback : undefined}
        closeDelay={closeDelay}
        closeOnClick={closeOnClick}
        delay={delay}
        render={children}
        onBlur={releaseClickLatch}
        onClick={armClickLatch}
        onPointerLeave={releaseClickLatch}
      />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          align={align}
          anchor={positionerAnchor}
          className="z-tooltip"
          side={placement}
          sideOffset={sideOffset}
        >
          <BaseTooltip.Popup
            className={cn(
              labelTypography,
              'rounded-sm border border-secondary-main bg-paper-elevation4 p-2 text-text-primary shadow-8',
              POPUP_ANIMATION,
              className,
            )}
          >
            {content}
            {arrow === true && (
              <BaseTooltip.Arrow className="flex data-[side=bottom]:top-[-11px] data-[side=left]:right-[-11px] data-[side=right]:left-[-11px] data-[side=top]:bottom-[-11px] data-[side=bottom]:rotate-180 data-[side=left]:-rotate-90 data-[side=right]:rotate-90 data-[side=top]:rotate-0">
                <ChevronDownIcon className="size-[20px] text-secondary-main" />
              </BaseTooltip.Arrow>
            )}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
